import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import './CareersPage.css';
import { PageHero } from '../../components/PageHero/PageHero';
import teamPic from '../../images/team-team.jpg';

const TruckIcon = () => (
  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M1 3h15v13H1zM16 8h4l3 3v5h-7V8z" />
    <circle cx="5.5" cy="18.5" r="2.5" /><circle cx="18.5" cy="18.5" r="2.5" />
  </svg>
);

const StarIcon = () => (
  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
  </svg>
);

const GrowthIcon = () => (
  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
    <polyline points="17 6 23 6 23 12" />
  </svg>
);

const PeopleIcon = () => (
  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 00-3-3.87" />
    <path d="M16 3.13a4 4 0 010 7.75" />
  </svg>
);

const PERKS = [
  {
    icon: <TruckIcon />,
    title: 'Work in a Real Operations Environment',
    body: 'You\'ll be working with live freight, real carriers, and actual shipments — not demos. Every decision you make moves cargo across the Southeast US.',
  },
  {
    icon: <GrowthIcon />,
    title: 'Room to Grow Fast',
    body: 'We\'re a lean team building a scalable logistics business. That means the people who join early get outsized responsibility, visibility, and advancement opportunities.',
  },
  {
    icon: <PeopleIcon />,
    title: 'Small Team, High Trust',
    body: 'No bureaucracy. No layers of approval for reasonable decisions. You\'ll have ownership over your work from day one and direct access to leadership.',
  },
  {
    icon: <StarIcon />,
    title: 'Competitive Compensation',
    body: 'Base salary aligned with market rates, performance bonuses tied to team outcomes, and benefits including health coverage and paid time off.',
  },
];

const OPEN_ROLES: {
  title: string;
  type: string;
  location: string;
  department: string;
  description: string;
}[] = [
  // Intentionally empty — will show "no open roles" UI
];

export const CareersPage = () => {
  const perksRef = useRef<HTMLElement>(null);
  const rolesRef = useRef<HTMLElement>(null);
  const ctaRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const sections = [perksRef, rolesRef, ctaRef];
    const observer = new IntersectionObserver(
      entries => entries.forEach(e => {
        if (e.isIntersecting) { e.target.classList.add('is-visible'); observer.unobserve(e.target); }
      }),
      { threshold: 0.08 }
    );
    sections.forEach(ref => {
      ref.current?.querySelectorAll<HTMLElement>('.rn-reveal')
        .forEach(el => observer.observe(el));
    });
    return () => observer.disconnect();
  }, []);

  return (
    <>
      <Helmet>
        <title>Careers — Renuit Logistics</title>
        <meta name="description" content="Join the Renuit Logistics team. We're building a transparent, tech-forward freight and 3PL company in the Southeast US. Open roles and speculative applications welcome." />
        <link rel="canonical" href="https://www.renuit.pro/careers" />
        <meta property="og:title" content="Careers — Renuit Logistics" />
        <meta property="og:description" content="Join the Renuit Logistics team. We're building a transparent, tech-forward freight and 3PL company in the Southeast US." />
        <meta property="og:url" content="https://www.renuit.pro/careers" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://www.renuit.pro/og-image.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@RenuitLogistics" />
        <meta name="twitter:title" content="Careers — Renuit Logistics" />
        <meta name="twitter:description" content="Join the Renuit Logistics team. We're building a transparent, tech-forward freight and 3PL company in the Southeast US." />
        <meta name="twitter:image" content="https://www.renuit.pro/og-image.jpg" />
      </Helmet>

      <PageHero
        tag="Join the Team"
        heading="Build the Future of Regional Logistics"
        subheading="We're growing a lean, high-trust team that moves real freight. If you care about operations, accountability, and doing logistics right — we want to hear from you."
        image={teamPic}
        imageAlt="Renuit Logistics team at work"
      />

      {/* Why Work Here */}
      <section className="rn-careers-perks" ref={perksRef}>
        <div className="rn-careers-perks__inner">
          <div className="rn-careers-perks__header rn-reveal">
            <span className="cs-topper">Why Renuit</span>
            <h2 className="rn-careers-perks__heading">What It's Like to Work Here</h2>
            <p className="rn-careers-perks__sub">
              We're not a large corporation — and that's the point. We move fast, act like owners,
              and build things that actually work in the real world.
            </p>
          </div>
          <ul className="rn-careers-perks__list">
            {PERKS.map(({ icon, title, body }) => (
              <li key={title} className="rn-careers-perk rn-reveal">
                <div className="rn-careers-perk__icon">{icon}</div>
                <h3 className="rn-careers-perk__title">{title}</h3>
                <p className="rn-careers-perk__body">{body}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Open Roles */}
      <section className="rn-careers-roles" ref={rolesRef}>
        <div className="rn-careers-roles__inner">
          <div className="rn-careers-roles__header rn-reveal">
            <span className="cs-topper">Open Positions</span>
            <h2 className="rn-careers-roles__heading">Current Openings</h2>
          </div>

          {OPEN_ROLES.length === 0 ? (
            <div className="rn-careers-empty rn-reveal">
              <div className="rn-careers-empty__icon" aria-hidden="true">📋</div>
              <h3 className="rn-careers-empty__heading">No open roles right now</h3>
              <p className="rn-careers-empty__body">
                We don't have any listed positions at the moment, but we're always interested
                in exceptional people. Send us your resume and tell us how you'd contribute —
                we read every message.
              </p>
              <a href="mailto:careers@renuit.pro" className="cs-button-solid">
                Send a Speculative Application
              </a>
            </div>
          ) : (
            <ul className="rn-careers-role-list">
              {OPEN_ROLES.map(({ title, type, location, department, description }) => (
                <li key={title} className="rn-careers-role rn-reveal">
                  <div className="rn-careers-role__meta">
                    <span className="rn-careers-role__dept">{department}</span>
                    <div className="rn-careers-role__tags">
                      <span className="rn-careers-role__tag">{type}</span>
                      <span className="rn-careers-role__tag">{location}</span>
                    </div>
                  </div>
                  <h3 className="rn-careers-role__title">{title}</h3>
                  <p className="rn-careers-role__desc">{description}</p>
                  <a href="mailto:careers@renuit.pro" className="rn-careers-role__apply">
                    Apply for this role →
                  </a>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="rn-careers-cta" ref={ctaRef}>
        <div className="rn-careers-cta__inner rn-reveal">
          <h2 className="rn-careers-cta__heading">Don't See the Right Fit?</h2>
          <p className="rn-careers-cta__sub">
            We hire for mindset as much as skill set. If you're tenacious, reliable, and
            excited about fixing logistics — reach out. We'll keep your details on file.
          </p>
          <div className="rn-careers-cta__actions">
            <a href="mailto:careers@renuit.pro" className="cs-button-solid">
              Email careers@renuit.pro
            </a>
            <Link to="/about" className="rn-careers-cta__link">Learn about the company →</Link>
          </div>
        </div>
      </section>
    </>
  );
};
